import { useMemo } from 'react';

import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import MoreHorizIcon from '@/material-icons/400-24px/more_horiz.svg?react';
import { openModal } from 'flavours/glitch/actions/modal';
import { Dropdown } from 'flavours/glitch/components/dropdown_menu';
import { Icon } from 'flavours/glitch/components/icon';
import { useIdentity } from 'flavours/glitch/identity_context';
import type { MenuItem } from 'flavours/glitch/models/dropdown_menu';
import {
  canManageReports,
  canViewAdminDashboard,
} from 'flavours/glitch/permissions';
import { useAppDispatch } from 'flavours/glitch/store';
import {
  getFiltersLink,
  getPrivacyLink,
  getStatusesCleanupLink,
  getSecurityLink,
  getExportLink,
  getAdminReportsLink,
  getAdminDashboardLink,
} from 'flavours/glitch/utils/backend_links';

const messages = defineMessages({
  blocks: { id: 'navigation_bar.blocks', defaultMessage: 'Blocked users' },
  domainBlocks: {
    id: 'navigation_bar.domain_blocks',
    defaultMessage: 'Blocked domains',
  },
  mutes: { id: 'navigation_bar.mutes', defaultMessage: 'Muted users' },
  filters: { id: 'navigation_bar.filters', defaultMessage: 'Muted words' },
  administration: {
    id: 'navigation_bar.administration',
    defaultMessage: 'Administration',
  },
  moderation: { id: 'navigation_bar.moderation', defaultMessage: 'Moderation' },
  logout: { id: 'navigation_bar.logout', defaultMessage: 'Logout' },
  automatedDeletion: {
    id: 'navigation_bar.automated_deletion',
    defaultMessage: 'Automated post deletion',
  },
  accountSettings: {
    id: 'navigation_bar.account_settings',
    defaultMessage: 'Password and security',
  },
  importExport: {
    id: 'navigation_bar.import_export',
    defaultMessage: 'Import and export',
  },
  privacyAndReach: {
    id: 'navigation_bar.privacy_and_reach',
    defaultMessage: 'Privacy and reach',
  },
});

export const MoreLink: React.FC = () => {
  const intl = useIntl();
  const { permissions } = useIdentity();
  const dispatch = useAppDispatch();

  const menu = useMemo(() => {
    const arr: MenuItem[] = [];

    // Backend pages: in standalone they point at the user's instance, or are
    // hidden when the instance software does not expose them. The `to:` items
    // (mutes/blocks/domain_blocks) are client-side routes and always available.
    const filtersLink = getFiltersLink();
    if (filtersLink) {
      arr.push({
        href: filtersLink,
        text: intl.formatMessage(messages.filters),
      });
    }

    arr.push(
      { to: '/mutes', text: intl.formatMessage(messages.mutes) },
      { to: '/blocks', text: intl.formatMessage(messages.blocks) },
      { to: '/domain_blocks', text: intl.formatMessage(messages.domainBlocks) },
    );

    const settingsItems: MenuItem[] = [];
    const privacyLink = getPrivacyLink();
    if (privacyLink) {
      settingsItems.push({
        href: privacyLink,
        text: intl.formatMessage(messages.privacyAndReach),
      });
    }
    const cleanupLink = getStatusesCleanupLink();
    if (cleanupLink) {
      settingsItems.push({
        href: cleanupLink,
        text: intl.formatMessage(messages.automatedDeletion),
      });
    }
    const securityLink = getSecurityLink();
    if (securityLink) {
      settingsItems.push({
        href: securityLink,
        text: intl.formatMessage(messages.accountSettings),
      });
    }
    const exportLink = getExportLink();
    if (exportLink) {
      settingsItems.push({
        href: exportLink,
        text: intl.formatMessage(messages.importExport),
      });
    }
    if (settingsItems.length > 0) {
      arr.push(null, ...settingsItems);
    }

    if (canManageReports(permissions)) {
      const reportsLink = getAdminReportsLink();
      if (reportsLink) {
        arr.push(null, {
          href: reportsLink,
          text: intl.formatMessage(messages.moderation),
        });
      }
    }

    if (canViewAdminDashboard(permissions)) {
      const dashboardLink = getAdminDashboardLink();
      if (dashboardLink) {
        arr.push({
          href: dashboardLink,
          text: intl.formatMessage(messages.administration),
        });
      }
    }

    const handleLogoutClick = () => {
      dispatch(openModal({ modalType: 'CONFIRM_LOG_OUT', modalProps: {} }));
    };

    arr.push(null, {
      text: intl.formatMessage(messages.logout),
      action: handleLogoutClick,
    });

    return arr;
  }, [intl, dispatch, permissions]);

  return (
    <Dropdown items={menu} placement='bottom-start'>
      <button className='column-link column-link--transparent' type='button'>
        <Icon id='' icon={MoreHorizIcon} className='column-link__icon' />

        <FormattedMessage id='navigation_bar.more' defaultMessage='More' />
      </button>
    </Dropdown>
  );
};
